import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { showDate, apiDateFormat } from '../../../../../utils';
import { POPUP_MSG } from '../../../../../constants';
import {
  calculateNextBatchNumber,
  calculateTotalSewDamagedQty,
  getAvailableSewCapacity,
  isPositiveInteger,
  isNonNegativeInteger,
} from '../utils/batchHelpers';

// ============================================
// DATE VALIDATION HELPERS
// ============================================

/**
 * Check if a date is in the future
 * @param {string} date - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is after today
 */
const isFutureDate = (date) => {
  if (!date) return false;
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today
  return new Date(date) > today;
};

/**
 * Check if date1 is on or before date2 (for sequential validation)
 * Returns true if either date is missing (skips validation)
 * @param {string} date1 - Earlier date (e.g., cut date)
 * @param {string} date2 - Later date (e.g., sew date)
 * @returns {boolean} True if date1 <= date2 or if either is null/undefined
 */
const isDateOnOrBefore = (date1, date2) => {
  if (!date1 || !date2) return true; // Skip validation if either date is missing
  // Handle dates that may include time component (e.g., "2024-01-15 00:00:00")
  const d1 = new Date(date1.split(' ')[0]);
  const d2 = new Date(date2.split(' ')[0]);
  return d1 <= d2;
};

/**
 * useBatchForm - Unified hook for managing all batch form state and validation
 *
 * Consolidates field management, validation, and payload building into a single hook.
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.lot - The lot object containing batch and quantity data
 * @param {Object|null} options.selectedBatch - The batch being edited (null for create mode)
 * @param {string} options.reportType - Type of report ('cutting' or 'sewing')
 * @param {Function} options.onFieldChange - Optional callback when any field changes
 * @returns {Object} Form state, handlers, validation, and payload builders
 */
export const useBatchForm = ({
  lot,
  selectedBatch = null,
  reportType = 'cutting',
  onFieldChange
} = {}) => {
  // Determine mode
  const isEditMode = !!selectedBatch;
  const isCreateMode = !selectedBatch;
  const isCuttingReport = reportType === 'cutting';
  const isSewingReport = reportType === 'sewing';

  // Calculate derived values (simple calculations, no memoization needed)
  const totalSewDamagedQty = calculateTotalSewDamagedQty(lot?.batch);
  const availableSewCapacity = getAvailableSewCapacity(lot, totalSewDamagedQty);
  const nextBatchNumber = calculateNextBatchNumber(lot);
  const selectedLot = lot?.selected_lot?.length ? lot.selected_lot[0] : null;

  // ============================================
  // FIELD STATE
  // ============================================

  // Cutting fields
  const [cutQty, setCutQty] = useState('');
  const [cutDmgQty, setCutDmgQty] = useState('');
  const [cutDate, setCutDate] = useState(showDate());

  // Sewing fields
  const [sewQty, setSewQty] = useState(selectedBatch?.sew_qty || '');
  const [sewDmgQty, setSewDmgQty] = useState(selectedBatch?.sew_damage_qty || '');
  const [sewDate, setSewDate] = useState(selectedBatch?.sew_date || showDate());

  // Packing fields
  const [packQty, setPackQty] = useState(selectedBatch?.sew_qty || '');
  const [packDate, setPackDate] = useState(selectedBatch?.pack_date || showDate());

  // Refs for focus management
  const cutQtyRef = useRef(null);
  const cutDmgQtyRef = useRef(null);
  const sewQtyRef = useRef(null);
  const sewDmgQtyRef = useRef(null);
  const packQtyRef = useRef(null);

  // ============================================
  // SYNC STATE WITH PROPS
  // ============================================

  // Sync sewing fields with selectedBatch changes
  useEffect(() => {
    if (isEditMode && selectedBatch) {
      if (selectedBatch.sew_qty !== undefined) {
        setSewQty(selectedBatch.sew_qty || '');
      }
      if (selectedBatch.sew_damage_qty !== undefined) {
        setSewDmgQty(selectedBatch.sew_damage_qty || '');
      }
      if (selectedBatch.sew_date && typeof selectedBatch.sew_date === 'string') {
        setSewDate(selectedBatch.sew_date.split(' ')[0]);
      }
      // Pack qty always equals sew qty
      if (selectedBatch.sew_qty && !selectedBatch.pack_qty) {
        setPackQty(selectedBatch.sew_qty);
      }
      if (selectedBatch.pack_date && typeof selectedBatch.pack_date === 'string') {
        setPackDate(selectedBatch.pack_date.split(' ')[0]);
      }
    }
  }, [isEditMode, selectedBatch]);

  // Initialize sew date min based on cut date
  // Uses functional update to avoid circular dependency (sewDate in deps AND updated)
  useEffect(() => {
    if (isEditMode && selectedBatch) {
      if (selectedBatch?.cut_date && typeof selectedBatch.cut_date === 'string') {
        // Set min date for sewing (must be after cutting)
        const cutDateStr = selectedBatch.cut_date.split(' ')[0];
        setSewDate(prev => (!prev || prev < cutDateStr) ? cutDateStr : prev);
      } else if (!selectedBatch?.sew_date) {
        setSewDate(showDate());
      }
    }
  }, [isEditMode, selectedBatch]);

  // ============================================
  // FIELD CHANGE HANDLERS WITH VALIDATION
  // ============================================

  const handleCutQtyChange = useCallback((value) => {
    const numValue = value === '' ? '' : +value;
    if (value === '' || isNonNegativeInteger(numValue)) {
      setCutQty(numValue);
      onFieldChange?.();
    }
  }, [onFieldChange]);

  const handleCutDmgQtyChange = useCallback((value) => {
    const numValue = value === '' ? '' : +value;
    if (value === '' || isNonNegativeInteger(numValue)) {
      setCutDmgQty(numValue);
      onFieldChange?.();
    }
  }, [onFieldChange]);

  const handleSewQtyChange = useCallback((value) => {
    const numValue = value === '' ? '' : +value;
    if (value === '' || isPositiveInteger(numValue)) {
      setSewQty(numValue);
      onFieldChange?.();
    }
  }, [onFieldChange]);

  const handleSewDmgQtyChange = useCallback((value) => {
    const numValue = value === '' ? '' : +value;
    if (value === '' || isNonNegativeInteger(numValue)) {
      setSewDmgQty(numValue);
      onFieldChange?.();
    }
  }, [onFieldChange]);

  const handlePackQtyChange = useCallback((value) => {
    const numValue = value === '' ? '' : +value;
    if (value === '' || isPositiveInteger(numValue)) {
      setPackQty(numValue);
      onFieldChange?.();
    }
  }, [onFieldChange]);

  const handleCutDateChange = useCallback((value) => {
    setCutDate(value);
    onFieldChange?.();
  }, [onFieldChange]);

  const handleSewDateChange = useCallback((value) => {
    setSewDate(value);
    onFieldChange?.();
  }, [onFieldChange]);

  const handlePackDateChange = useCallback((value) => {
    setPackDate(value);
    onFieldChange?.();
  }, [onFieldChange]);

  // ============================================
  // VALIDATION
  // ============================================

  /**
   * Validate cutting report fields
   * @returns {{ valid: boolean, message?: string }}
   */
  const validateCuttingReport = useCallback(() => {
    if (!cutQty && cutQty !== 0) {
      return { valid: false, message: 'Please enter cut quantity' };
    }
    if (cutQty < 0) {
      return { valid: false, message: POPUP_MSG.INVALID_QTY_MSG };
    }
    // Date validation: cut date cannot be in the future
    if (isFutureDate(cutDate)) {
      return { valid: false, message: 'Cut date cannot be in the future' };
    }
    return { valid: true };
  }, [cutQty, cutDate]);

  /**
   * Validate sewing report fields (create mode)
   * @returns {{ valid: boolean, message?: string }}
   */
  const validateSewingReport = useCallback(() => {
    if (sewQty < 1) {
      return { valid: false, message: 'Sew quantity must be at least 1' };
    }
    const totalSew = (+sewQty || 0) + (+sewDmgQty || 0);
    if (totalSew > availableSewCapacity) {
      return {
        valid: false,
        message: `Total sew quantity (${totalSew}) exceeds available cut capacity (${availableSewCapacity})`
      };
    }
    // Date validation: sew date cannot be in the future
    if (isFutureDate(sewDate)) {
      return { valid: false, message: 'Sew date cannot be in the future' };
    }
    return { valid: true };
  }, [sewQty, sewDmgQty, availableSewCapacity]);

  /**
   * Validate sewing update fields (edit mode)
   * @returns {{ valid: boolean, message?: string }}
   */
  const validateSewingUpdate = useCallback(() => {
    if (sewQty < 1) {
      return { valid: false, message: 'Sew quantity must be at least 1' };
    }
    const totalSew = (+sewQty || 0) + (+sewDmgQty || 0);
    if (totalSew > availableSewCapacity) {
      return {
        valid: false,
        message: `Total sew quantity (${totalSew}) exceeds available capacity (${availableSewCapacity})`
      };
    }
    // Date validation: sew date cannot be in the future
    if (isFutureDate(sewDate)) {
      return { valid: false, message: 'Sew date cannot be in the future' };
    }
    // Sequential validation: sew date must be on or after cut date (if cut date exists)
    // Sewing reports may skip cutting, so only validate if cut_date is present
    if (selectedBatch?.cut_date && !isDateOnOrBefore(selectedBatch.cut_date, sewDate)) {
      return { valid: false, message: 'Sew date cannot be before cut date' };
    }
    return { valid: true };
  }, [sewQty, sewDmgQty, availableSewCapacity]);

  /**
   * Validate packing fields
   * @returns {{ valid: boolean, message?: string }}
   */
  const validatePacking = useCallback(() => {
    if (packQty < 1) {
      return { valid: false, message: 'Pack quantity must be at least 1' };
    }
    // Date validation: pack date cannot be in the future
    if (isFutureDate(packDate)) {
      return { valid: false, message: 'Package date cannot be in the future' };
    }
    // Sequential validation: pack date must be on or after sew date
    // Use sewDate from form state, or selectedBatch.sew_date as fallback
    const effectiveSewDate = sewDate || selectedBatch?.sew_date;
    if (!isDateOnOrBefore(effectiveSewDate, packDate)) {
      return { valid: false, message: 'Package date cannot be before sew date' };
    }
    return { valid: true };
  }, [packQty]);

  /**
   * Validate lot selection
   * @returns {{ valid: boolean, message?: string }}
   */
  const validateLotSelection = useCallback(() => {
    if (!selectedLot?.name && lot?.ready_status === 'ready') {
      return { valid: false, message: POPUP_MSG.SELECT_LOT };
    }
    return { valid: true };
  }, [selectedLot?.name, lot?.ready_status]);

  /**
   * Main validation function - validates based on current mode and report type
   * @param {string} field - Optional field to validate ('sew', 'pack'), validates create mode if not provided
   * @returns {{ valid: boolean, message?: string }}
   */
  const validate = useCallback((field) => {
    // For edit mode field-specific validation
    if (field === 'sew') {
      return validateSewingUpdate();
    }
    if (field === 'pack') {
      return validatePacking();
    }

    // For create mode
    if (isCreateMode) {
      const lotValidation = validateLotSelection();
      if (!lotValidation.valid) return lotValidation;

      if (isCuttingReport) {
        return validateCuttingReport();
      }
      if (isSewingReport) {
        return validateSewingReport();
      }
    }

    return { valid: true };
  }, [
    isCreateMode,
    isCuttingReport,
    isSewingReport,
    validateLotSelection,
    validateCuttingReport,
    validateSewingReport,
    validateSewingUpdate,
    validatePacking
  ]);

  // ============================================
  // PAYLOAD BUILDERS
  // ============================================

  /**
   * Build payload for creating a new batch
   * @returns {Object} Upsert payload for batch creation
   */
  const buildCreatePayload = useCallback(() => {
    const payload = {
      move_id: [lot.move_id],
      data: {
        lot: selectedLot?.name || '',
        is_default_lot: !!lot?.selected_lot?.[0]?.name,
        batch_no: `${lot.lot_no}-${nextBatchNumber}`,
      },
    };

    if (isCuttingReport) {
      payload.data.order_qty = +cutQty;
      payload.data.cut_qty = +cutQty;
      payload.data.cut_damage_qty = +cutDmgQty || 0;
      payload.data.cut_date = apiDateFormat(cutDate || showDate());
    } else if (isSewingReport) {
      // Sewing-only batch
      payload.data.order_qty = +sewQty;
      payload.data.cut_qty = 0;
      payload.data.cut_damage_qty = 0;
      payload.data.sew_qty = +sewQty;
      payload.data.sew_damage_qty = +sewDmgQty || 0;
      payload.data.sew_date = apiDateFormat(sewDate || showDate());
    }

    return payload;
  }, [
    lot,
    selectedLot,
    nextBatchNumber,
    isCuttingReport,
    isSewingReport,
    cutQty,
    cutDmgQty,
    cutDate,
    sewQty,
    sewDmgQty,
    sewDate
  ]);

  /**
   * Build payload for updating an existing batch
   * Backend uses (move_id, batch_no) as unique key to find and update batch
   *
   * @param {string} field - The field being updated ('sew' or 'pack')
   * @returns {Object|null} Upsert payload for batch update, or null if validation fails
   */
  const buildUpdatePayload = useCallback((field) => {
    if (!selectedBatch?.name) return null;

    let updateData = {
      // batch_no is REQUIRED - backend uses (move_id, batch_no) to find existing batch
      batch_no: selectedBatch.name,
    };

    if (field === 'sew') {
      updateData.sew_qty = parseFloat(sewQty);
      updateData.sew_damage_qty = parseFloat(sewDmgQty) || 0;
      updateData.sew_date = apiDateFormat(sewDate || showDate());
    } else if (field === 'pack') {
      updateData.pack_qty = parseFloat(packQty);
      updateData.pack_date = apiDateFormat(packDate || showDate());
    }

    return {
      move_id: [lot.move_id],
      data: updateData
    };
  }, [lot, selectedBatch, sewQty, sewDmgQty, sewDate, packQty, packDate]);

  // ============================================
  // FOCUS MANAGEMENT
  // ============================================

  /**
   * Focus on the appropriate field based on mode and report type
   * @param {string} field - Optional field to focus ('sew', 'pack')
   */
  const focusField = useCallback((field) => {
    if (field === 'sew') {
      sewQtyRef.current?.focus();
    } else if (field === 'pack') {
      packQtyRef.current?.focus();
    } else if (isCreateMode) {
      if (isCuttingReport) {
        cutQtyRef.current?.focus();
      } else if (isSewingReport) {
        sewQtyRef.current?.focus();
      }
    }
  }, [isCreateMode, isCuttingReport, isSewingReport]);

  // ============================================
  // COMPUTED VALUES
  // ============================================

  /**
   * Check if the form has valid data (basic check, not full validation)
   */
  const isValid = useMemo(() => {
    if (isCreateMode) {
      if (isCuttingReport) {
        return cutQty > 0 && cutQty <= (lot.total_qty - lot.ready_qty);
      }
      if (isSewingReport) {
        return sewQty > 0 && (sewQty + (sewDmgQty || 0)) <= availableSewCapacity;
      }
    }
    return true;
  }, [isCreateMode, isCuttingReport, isSewingReport, cutQty, sewQty, sewDmgQty, lot, availableSewCapacity]);

  /**
   * Get min date for sewing (based on cut date)
   */
  const sewMinDate = useMemo(() => {
    if (selectedBatch?.cut_date && typeof selectedBatch.cut_date === 'string') {
      return selectedBatch.cut_date.split(' ')[0];
    }
    return undefined;
  }, [selectedBatch?.cut_date]);

  /**
   * Get min date for packing (based on sew date)
   */
  const packMinDate = useMemo(() => {
    if (selectedBatch?.sew_date && typeof selectedBatch.sew_date === 'string') {
      return selectedBatch.sew_date.split(' ')[0];
    }
    return undefined;
  }, [selectedBatch?.sew_date]);

  // ============================================
  // RETURN API
  // ============================================

  return {
    // Mode flags
    isEditMode,
    isCreateMode,
    isCuttingReport,
    isSewingReport,

    // Computed values
    totalSewDamagedQty,
    availableSewCapacity,
    nextBatchNumber,
    selectedLot,
    isValid,

    // Field values and handlers (organized by section)
    fields: {
      cutting: {
        qty: cutQty,
        dmgQty: cutDmgQty,
        date: cutDate,
        onQtyChange: handleCutQtyChange,
        onDmgQtyChange: handleCutDmgQtyChange,
        onDateChange: handleCutDateChange,
        qtyRef: cutQtyRef,
        dmgQtyRef: cutDmgQtyRef,
      },
      sewing: {
        qty: sewQty,
        dmgQty: sewDmgQty,
        date: sewDate,
        onQtyChange: handleSewQtyChange,
        onDmgQtyChange: handleSewDmgQtyChange,
        onDateChange: handleSewDateChange,
        qtyRef: sewQtyRef,
        dmgQtyRef: sewDmgQtyRef,
        minDate: sewMinDate,
      },
      packing: {
        qty: packQty,
        date: packDate,
        onQtyChange: handlePackQtyChange,
        onDateChange: handlePackDateChange,
        qtyRef: packQtyRef,
        minDate: packMinDate,
      },
    },

    // Validation
    validate,
    validateCuttingReport,
    validateSewingReport,
    validateSewingUpdate,
    validatePacking,

    // Payload builders
    buildCreatePayload,
    buildUpdatePayload,

    // Focus management
    focusField,
  };
};
